/**
 * Reads the fragment model chunks to merge then into a global one
 *
 * @module app/components/ModelComposer
 */
import _ from 'lodash';
import DomPushAdapter from './push/domUpdates/DomPushAdapter';
import jQuery from 'jquery';
import MessageBusTopics from '../../common/MessageBusTopics';
import PushAdapter from './push/PushAdapter';
import SportsDataRepository from './SportsDataRepository';

/**
 * Order of fields in hash
 */
var modelHashFields = window.WH.sportsbook.frontEndConfig.modelHashFields;

/**
 * Remove selection from model
 *
 * @param   {String} selection
 *
 * @returns {void}
 *
 * @private
 */
var removeSelectionFromModel = function (selection) {

    if (window.WH.sportsbook.model.selections[selection]) {
        DomPushAdapter.unsubscribe(selection);
        PushAdapter.unsubscribe(window.WH.sportsbook.model.selections[selection].pdsTopic);
        delete window.WH.sportsbook.model.selections[selection];
    }
};

/**
 * Remove market from model
 *
 * @param   {String} selection
 *
 * @returns {void}
 *
 * @private
 */
var removeMarketFromModel = function (market) {

    if (window.WH.sportsbook.model.markets[market]) {
        _.each(window.WH.sportsbook.model.markets[market].selections, removeSelectionFromModel);
        DomPushAdapter.unsubscribe(market);
        PushAdapter.unsubscribe(window.WH.sportsbook.model.markets[market].pdsTopic);
        delete window.WH.sportsbook.model.markets[market];
    }
};

/**
 * Extract data from hash and join to item as properties
 *
 * @param {Object} item
 * @param {String} itemId
 * @param {String} type One of: events, markets, selections, competitions
 */
var expandHashData = function expandHashData(item, itemId, type) {

    var ancestors = item.topic.split('/').slice(1);
    var hashItems = item.hash;

    _.extend(
        item,
        {
            id: itemId,
            pdsTopic: item.topic,
            eventId: ancestors[0],
            marketId: ancestors[1],
        }
    );

    /**
     * Based on field and it's index assign property with value from hash array by index
     *
     * @param {String} field field from hash array
     * @param {Number} index index
     *
     * @returns {void}
     */
    var assignItem = function (field, index) {

        item[field] = hashItems[index];
    };

    _.each(modelHashFields[type], assignItem);

    delete item.hash;

};

/**
 * Iterate over items in group. If hash is provided,
 * actual item is extended with hash values and other necessary values that can be computed based on it.
 *
 * @param {Object} group Group of events/markets/selections
 * @param {String} type Type of group
 *
 * @returns {Object} Group of events/markets/selections
 */
var expandHashDataInGroup = function joinHashData(group, type) {

    /**
     * If there is hash in item properties we expand it to properties
     *
     * @param {Object} item item from model
     * @param {String} itemId
     *
     * @returns {void}
     */
    var loopGroups = function loopGroups(item, itemId) {

        if (item.hash) {

            expandHashData(item, itemId, type);
        }
    };

    _.each(group, loopGroups);

    return group;
};

/**
 * Remove event from model
 *
 * @param   {String} selection
 *
 * @returns {void}
 *
 * @private
 */
var removeEventFromModel = function (evt) {

    if (window.WH.sportsbook.model.events[evt]) {
        _.each(window.WH.sportsbook.model.events[evt].markets, removeMarketFromModel);
        DomPushAdapter.unsubscribe(evt);
        PushAdapter.unsubscribe(window.WH.sportsbook.model.events[evt].pdsTopic);
        delete window.WH.sportsbook.model.events[evt];
    }
};

/**
 * Public functions.
 *
 * @type {Object}
 */
var api = {

    /**
     * Guess what, it regenerates the WH sportsbook model :)
     *
     * @param   {Object} model The optional parameter, which is model to extend
     *
     * @returns {void}
     *
     * @public
     */
    regenerateMergedModel: function (model) {

        window.WH.sportsbook.model = model || {};
        api.generateMergedModel(model);
    },

    /**
     * This function will return a model object composed out of all the chunks taken from
     * each page fragment containing events.
     *
     * @param   {Object} model The optional parameter, which is model to extend
     *
     * @returns {void}
     *
     * @public
     */
    generateMergedModel: function (model) {

        window.WH.messageBus.publish(
            MessageBusTopics.MODEL_COMPOSER_PREGENERATE_MERGED_MODEL,
            _.clone(window.WH.sportsbook.modelBox)
        );

        var modelChunks = window.WH.sportsbook.modelBox;

        var newModel = model || {
            'selections': {},
            'markets': {},
            'events': {},
            'competitions': {},
        };

        _.each(modelChunks, function (chunk, chunkKey) {

            newModel = jQuery.extend(true, newModel, chunk);
            delete modelChunks[chunkKey];
        });

        api.convertHashModel(newModel);

        SportsDataRepository.setModel(newModel);
        window.WH.sportsbook.model = SportsDataRepository.getModel();
    },

    /**
     * Convert model with hash value to full model.
     * Extract values from hash and fill children ids
     *
     * @param {Object} model Actual WH.sportsbook.model
     *
     * @returns {Object} Converted model
     */
    convertHashModel: function convertHashModel(model) {

        /**
         * For given type load item children ids (markets for events, selections for markets)
         *
         * @param {String} type markets or selections
         * @param {Object} item object missing children
         * @param {String} key item key
         *
         * @returns {void}
         */
        var assignChildren = function assignChildren(type, item, key) {

            if (item[type]) {

                return;
            }

            item[type] = _.map(
                _.filter(model[type], { parent: key }),
                'id'
            );
        };

        if (!model) {

            return {
                events: {},
                markets: {},
                selections: {},
            };
        }

        _.each(model, expandHashDataInGroup);
        _.each(model.events, _.partial(assignChildren, 'markets'));
        _.each(model.markets, _.partial(assignChildren, 'selections'));

        return model;
    },

    /**
     * Convert modelBox with hash value to full model.
     * Extract values from hash and fill children ids
     *
     * @param {Object} modelBox Actual WH.sportsbook.modelBox
     *
     * @returns {Object} Converted modelBox
     */
    convertHashModelBox: function convertHashModelBox(modelBox) {

        _.each(modelBox, api.convertHashModel);

        return modelBox;
    },

    /**
     * Extend model with new data
     *
     * @param   {Object} data
     *
     * @returns {void}
     *
     * @private
     */
    extendModel: function (data) {

        api.convertHashModel(data);
        SportsDataRepository.setModel(jQuery.extend(true, window.WH.sportsbook.model, data));
    },

    /**
     * Removes events from model
     *
     * @param   {Array} eventsArray
     *
     * @returns {void}
     *
     * @private
     */
    removeEventsFromModel: function (eventsArray) {
        _.each(eventsArray, removeEventFromModel);

        SportsDataRepository.setModel(window.WH.sportsbook.model);
        window.WH.sportsbook.model = SportsDataRepository.getModel();
    },

    /**
     * Removes markets from model
     *
     * @param   {Array} marketsArray
     *
     * @returns {void}
     *
     * @private
     */
    removeMarketsFromModel: function (marketsArray) {

        _.each(marketsArray, removeMarketFromModel);

        SportsDataRepository.setModel(window.WH.sportsbook.model);
        window.WH.sportsbook.model = SportsDataRepository.getModel();
    },
};

export default api;
